import { Button, DisplayInfo, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';

import Loading from '../../../components/info/Loading';
import SectionTitle from '../../../components/text/SectionTitle';

import {
  DebtPublicationRun,
  getConfig,
  getRunFileUrl,
  getRuns,
  runNow,
} from './api';
import { RunTable } from './components/RunTable';

const dictPrefix = 'financialPortal.debtPublication';
const ITEMS_PER_PAGE = 10;

export default function DebtPublicationView() {
  const { t } = useTranslation();

  const [configLoading, setConfigLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  const [runs, setRuns] = useState<DebtPublicationRun[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  // Bumped after a manual run so the runs list always refetches, even when
  // the user was already on page 0 (where `setPage(0)` alone is a no-op and
  // wouldn't otherwise change the fetch effect's dependencies). Mirrors the
  // `reloadKey` pattern used by AdmissionAndEnrollments/TariffAndTuition/
  // Tariff/index.tsx.
  const [refreshToken, setRefreshToken] = useState(0);

  // Config is fetched once on mount: it decides whether this tenant even
  // has a debt-publication provider configured (HU brief §config).
  useEffect(() => {
    let mounted = true;
    getConfig().then(({ data, error }) => {
      if (!mounted) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.configError`),
        });
      } else {
        setEnabled(data.enabled);
      }
      setConfigLoading(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetches the run list. Deliberately depends only on the values that
  // should actually cause a (re)fetch: the page, the enabled/config-loading
  // gate, and `refreshToken` (bumped after a manual run so the list always
  // refreshes, even when `page` doesn't change — see handleRun). `t` is used
  // only for the error-toast copy and is intentionally NOT a dependency:
  // react-i18next's `t` identity is not guaranteed stable across renders
  // (and in tests a naive mock returns a brand-new function every render),
  // so including it here would re-fire this effect on every render and loop
  // forever. Reading `t` from the closure is safe because this effect only
  // *runs* when one of its real triggers changes, at which point it reads
  // whatever `t` currently is.
  useEffect(() => {
    if (configLoading || !enabled) {
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const { data, error } = await getRuns(page, ITEMS_PER_PAGE);
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setRuns(data.data);
        setTotalItems(data.total);
        setTotalPages(data.total_pages);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading, enabled, page, refreshToken]);

  // Only resets `page` (and bumps `refreshToken`) instead of calling the
  // fetch directly here — the fetch effect above is memoized on the
  // pre-click `page`, so an explicit call would race it and could fetch a
  // stale page. See PaymentHistory.tsx for the same pattern.
  const handleRun = useCallback(async () => {
    setIsRunning(true);
    const { data, error } = await runNow(false);
    setIsRunning(false);
    if (error || !data || data.status === 'failed') {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`${dictPrefix}.runError`),
      });
    } else {
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.runSuccess`, { fileName: data.fileName }),
      });
    }
    // These two updates land after an `await`, i.e. outside React 17's
    // automatic event-handler batching: without `unstable_batchedUpdates`
    // they'd each trigger their own render and thus their own run of the
    // fetch effect above — a duplicate `getRuns(0, ITEMS_PER_PAGE)` on top
    // of the correct one. Batching them collapses that into a single render
    // (page: n -> 0, refreshToken: n -> n+1 together) and a single fetch.
    unstable_batchedUpdates(() => {
      setPage(0);
      setRefreshToken((prev) => prev + 1);
    });
  }, [t]);

  const handleDownload = useCallback(
    async (row: DebtPublicationRun) => {
      const { data, error } = await getRunFileUrl(row.id);
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.downloadError`),
        });
        return;
      }
      window.open(data.url, '_blank', 'noopener');
    },
    [t],
  );

  return (
    <Card className="mt-5 mx-3 p-4">
      {configLoading ? (
        <Loading insideCard />
      ) : (
        <>
          <Row className="align-items-center">
            <Col xs={12} md={8}>
              <SectionTitle text={t(`${dictPrefix}.title`)} />
              <p>{t(`${dictPrefix}.subtitle`)}</p>
            </Col>
            {enabled ? (
              <Col xs={12} md={4} className="text-md-right">
                <Button
                  text={t(`${dictPrefix}.runNow`)}
                  loading={isRunning}
                  disabled={isRunning}
                  onClick={handleRun}
                  size="md"
                  className="mb-1"
                />
              </Col>
            ) : null}
          </Row>
          {enabled ? (
            <RunTable
              data={runs}
              isLoadingResults={isLoading}
              totalItems={totalItems}
              totalPages={totalPages}
              currentPage={page}
              itemsPerPage={ITEMS_PER_PAGE}
              onChangePage={setPage}
              onDownload={handleDownload}
            />
          ) : (
            <DisplayInfo
              iconName="information"
              textBody={t(`${dictPrefix}.disabled`)}
              maxWidth="650px"
            />
          )}
        </>
      )}
    </Card>
  );
}
